/**
 * Accessible & Reusable Tabs Initializer
 * ----------------------------------------------------
 * Works with templates using WAI-ARIA 1.2 compliant markup:
 * - role="tablist", role="tab", role="tabpanel"
 * - aria-selected, aria-controls, aria-labelledby, hidden
 *
 * Supported data attributes on the .tabs-header element:
 * ----------------------------------------------------
 * - data-lazy-init="tabs"      (required)
 * - data-allow-scroll="true"   → page scrolls to active tab on click
 * - data-scroll-on-init="true" → scrolls to active tab on init
 * - data-save-history="true"   → saves active tab in sessionStorage + URL hash
 * - data-top-offset="NUMBER"          → offset in px for scroll position
 * - data-scroll-to-selector="SELECTOR" → CSS selector of the element to scroll to instead of
 *                                         the active tab button (e.g. the section heading above
 *                                         the tabs); falls back to tab button if not found
 *
 * Keyboard support (WAI-ARIA pattern):
 * - ← / → arrows to switch focus between tabs
 * - Home / End jumps to first / last tab
 * - Enter / Space activates focused tab
 */
export function initTabs(component) {
	// --- 1. Setup ---
	const settings = {
		allowScroll: component.dataset.allowScroll === 'true',
		scrollOnInit: component.dataset.scrollOnInit === 'true',
		saveHistory: component.dataset.saveHistory === 'true',
		topOffset: parseInt(component.dataset.topOffset, 10) || 0,
		scrollToSelector: component.dataset.scrollToSelector || null,
	};

	const container = component.closest('.container, .content-section') || component.parentElement;
	const triggers = Array.from(component.querySelectorAll('[role="tab"][data-tab]'));
	const panelSelector = triggers.map(t => t.dataset.tab).join(',');
	const panels = panelSelector ? Array.from(document.querySelectorAll(panelSelector)) : [];

	if (!triggers.length || !panels.length) {
		console.warn('Tabs component has no valid triggers or panels:', component);
		return;
	}

	// --- 2. Helpers ---

	/** Smooth scroll respecting topOffset; uses scrollToSelector if defined */
	function smoothScrollTo(triggerEl) {
		if (!settings.allowScroll) return;
		const targetEl = settings.scrollToSelector
			? (document.querySelector(settings.scrollToSelector) || triggerEl)
			: triggerEl;
		if (!targetEl) return;
		requestAnimationFrame(() => {
			const rect = targetEl.getBoundingClientRect();
			const targetY = rect.top + window.pageYOffset - settings.topOffset;
			window.scrollTo({ top: targetY, behavior: 'smooth' });
		});
	}

	/** Ensure active tab button is visible in scrollable header */
	function scrollActiveTabIntoView(triggerEl) {
		if (!triggerEl) return;
		const row = component.querySelector('.tabs-header__row');
		if (!row) return;
		const parentRect = row.getBoundingClientRect();
		const childRect = triggerEl.getBoundingClientRect();

		if (childRect.left < parentRect.left + 8 || childRect.right > parentRect.right - 8) {
			row.scrollTo({
				left: row.scrollLeft + (childRect.left - parentRect.left) - 40,
				behavior: 'smooth',
			});
		}
	}

	/** Update ARIA & visibility states */
	function updateAriaStates(activeTrigger, activePanel) {
		triggers.forEach(btn => {
			const isActive = btn === activeTrigger;
			btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
			btn.setAttribute('tabindex', isActive ? '0' : '-1');
			if (isActive) btn.setAttribute('aria-current', 'true');
			else btn.removeAttribute('aria-current');
		});

		panels.forEach(panel => {
			const isActive = panel === activePanel;
			panel.classList.toggle('active', isActive);
			panel.classList.toggle('in', isActive);
			panel.hidden = !isActive;
			panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
		});
	}

	// --- 3. Core activation logic ---
	function activateTab(triggerToActivate, options = {}) {
		if (!triggerToActivate) return;
		const { doScroll = false, updateHistory = true } = options;
		const panelId = triggerToActivate.dataset.tab;
		const panelToActivate = document.querySelector(panelId);
		if (!panelToActivate) return;

		// deactivate old visual states
		triggers.forEach(t => t.classList.remove('active', 'in'));

		// activate new trigger
		triggerToActivate.classList.add('active', 'in');
		updateAriaStates(triggerToActivate, panelToActivate);

		// container state class
		if (container) {
			Array.from(container.classList)
				.filter(c => c.startsWith('active-tab-'))
				.forEach(c => container.classList.remove(c));
			container.classList.add(`active-tab-${panelId.replace('#', '')}`);
		}

		// history persistence
		if (settings.saveHistory && updateHistory) {
			const storageKey = `active-tab:${component.id || location.pathname}`;
			sessionStorage.setItem(storageKey, panelId);
			history.replaceState(null, '', panelId);
		}

		requestAnimationFrame(() => scrollActiveTabIntoView(triggerToActivate));
		if (doScroll && settings.allowScroll) smoothScrollTo(triggerToActivate);
	}

	// --- 4. Event listeners ---

	// Click activation
	triggers.forEach(trigger => {
		trigger.addEventListener('click', e => {
			e.preventDefault();
			if (trigger.getAttribute('aria-selected') === 'true') return;
			activateTab(trigger, { doScroll: true });
		});
	});

	// Keyboard navigation (Arrow keys + Home/End + Enter/Space)
	component.addEventListener('keydown', e => {
		const current = document.activeElement;
		if (!current || current.getAttribute('role') !== 'tab') return;

		let newIndex = triggers.indexOf(current);
		switch (e.key) {
			case 'ArrowRight':
			case 'ArrowDown':
				newIndex = (newIndex + 1) % triggers.length;
				break;
			case 'ArrowLeft':
			case 'ArrowUp':
				newIndex = (newIndex - 1 + triggers.length) % triggers.length;
				break;
			case 'Home':
				newIndex = 0;
				break;
			case 'End':
				newIndex = triggers.length - 1;
				break;
			case 'Enter':
			case ' ':
				e.preventDefault();
				activateTab(current, { doScroll: true });
				return;
			default:
				return;
		}
		e.preventDefault();
		triggers[newIndex].focus();
	});

	// --- 5. Initialization ---
	function initialize() {
		let initialTabId = triggers[0]?.dataset.tab;
		const storageKey = `active-tab:${component.id || location.pathname}`;
		const hash = window.location.hash;
		const storedId = sessionStorage.getItem(storageKey);

		const isValidTab = id => triggers.some(t => t.dataset.tab === id);

		if (settings.saveHistory) {
			if (hash && isValidTab(hash)) initialTabId = hash;
			else if (storedId && isValidTab(storedId)) initialTabId = storedId;
		}

		const initialTrigger =
			triggers.find(t => t.dataset.tab === initialTabId) || triggers[0];

		activateTab(initialTrigger, {
			doScroll: settings.scrollOnInit,
			updateHistory: false,
		});
	}

	initialize();
}
